import { isEqual } from 'lodash';
import type { ExpressionSpecification } from 'mapbox-gl';
import { MapManager } from '@/core/modules/MapManager';
import { type ColorScheme, DEFAULT_INCIDENT_COLOR_KEY } from '../../SituationalAwareness.types';
import { AssetsMapDrawer } from '@/components/composite/GeoMap/drawers/AssetsMapDrawer';
import { ClusterDonutMarkers, ClusterDonutMarkersProps } from './MapDataDrawer.ClusterDonutMarkers';
import { LAYER, SOURCE } from './MapDataDrawer.constants';
import {
  type LatLng,
  applyStaticEventCoords,
  createPopupContentElement,
} from '@/components/composite/GeoMap/mapImages';
import {
  ClusterTypes,
  IncidentFeatureItemProperties,
  IncidentsGeoJsonData,
  RenderAssetsItem,
  RenderIncidentsItem,
} from './MapDataDrawer.types';
import { findLocationsCenter, generateClusterIncidentProperties, getLayerMetadata } from './MapDataDrawer.utils';

const OPACITY = 0.8;
export const CLUSTER_MAX_ZOOM = 10;

// README: Examples
// https://docs.mapbox.com/mapbox-gl-js/example/cluster/
// https://docs.mapbox.com/mapbox-gl-js/example/cluster-html/

export class MapDataDrawer {
  map: MapManager | undefined;
  donutMarkers: ClusterDonutMarkers | undefined;
  private incidentListeners: { dispose(): void }[] = [];
  private readonly assetsDrawer: AssetsMapDrawer | undefined;
  protected visible = true;

  constructor(opts: { assetsDrawer?: AssetsMapDrawer | null } = {}) {
    this.assetsDrawer = opts.assetsDrawer === undefined ? new AssetsMapDrawer() : opts.assetsDrawer ?? undefined;
  }

  protected getOwnedLayerIds(): string[] {
    return [LAYER.INCIDENT_CLUSTERS];
  }

  protected getOwnedSourceIds(): string[] {
    return [SOURCE.INCIDENT_CLUSTERS];
  }

  private applyVisibility(layerId: string): void {
    this.map?.setLayoutProperty(layerId, 'visibility', this.visible ? 'visible' : 'none');
  }

  setMap = (map: MapManager) => {
    this.map = map;
    this.assetsDrawer?.setMap(map);
    return this;
  };

  hasMap = () => !!this.map;

  resetActiveTargets = () => this.assetsDrawer?.resetActiveTargets();

  renderAssets = (args: Parameters<AssetsMapDrawer['renderAssets']>[0]) => this.assetsDrawer?.renderAssets(args);

  renderSelectedAssetProximity = (assets: RenderAssetsItem[]) => this.assetsDrawer?.renderSelectedAssetProximity(assets);

  private getIncidentsGeoJsonData = (args: {
    items: RenderIncidentsItem[];
    colorScheme: ColorScheme;
    enrichProps?(item: RenderIncidentsItem): Record<string, unknown>;
  }): IncidentsGeoJsonData => {
    const colorKey = args.colorScheme.key || DEFAULT_INCIDENT_COLOR_KEY;
    const getColorTypeValue = (item: RenderIncidentsItem) => {
      const value = parseInt(item[colorKey as keyof RenderIncidentsItem] as string, 10).toString();
      return args.colorScheme.scheme?.get(value) || 'white';
    };

    const data: IncidentsGeoJsonData = {
      type: 'FeatureCollection',
      features: args.items.map((item) => ({
        type: 'Feature',
        id: item.id,
        properties: {
          ...item,
          color: getColorTypeValue(item),
          ...(args.enrichProps ? args.enrichProps(item) : null),
        } satisfies IncidentFeatureItemProperties,
        geometry: {
          type: 'Point',
          coordinates: [item.longitude, item.latitude],
        },
      })),
    };

    return data;
  };

  initClusterDonutMarkers = ({
    items,
    colorScheme,
    onClusterClick,
  }: Pick<ClusterDonutMarkersProps, 'items' | 'onClusterClick' | 'colorScheme'>) => {
    this.donutMarkers =
      this.donutMarkers ||
      new ClusterDonutMarkers({
        items,
        mapManager: this.map!,
        source: SOURCE.INCIDENT_CLUSTERS,
        layer: LAYER.INCIDENT_CLUSTERS,
        colorScheme,
        onClusterClick,
        visible: this.visible,
      });

    this.donutMarkers.setItems({ items });
    return this.donutMarkers;
  };

  removeClusterDonutMarkers = () => {
    this.donutMarkers?.remove();
    this.donutMarkers = undefined;
  };

  renderIncidentDonutClusters = (args: {
    items: RenderIncidentsItem[];
    withCentrify?: boolean;
    onClick: (item: RenderIncidentsItem, options: { ids: RenderIncidentsItem['id'][]; locations: LatLng[] }) => void;
    onClusterClick: ClusterDonutMarkersProps['onClusterClick'];
    colorScheme: ColorScheme;
  }) => {
    if (!this.map) return;

    const layerMetaData = getLayerMetadata<{ colorSchemaKey?: string }>(this.map.getMap()!, LAYER.INCIDENT_CLUSTERS);
    const hasColorSchemeChanged = !isEqual(layerMetaData?.colorSchemaKey, args.colorScheme.key);
    /**
     * DOCS:
     * If you still see clusters in old positions after calling setData,
     * try removing and re-adding the source and layers as a workaround.
     */
    let donutMarkers = this.initClusterDonutMarkers({
      items: args.items,
      onClusterClick: args.onClusterClick,
      colorScheme: args.colorScheme,
    });
    if ((!donutMarkers.isEqualItems() && donutMarkers.isInit()) || hasColorSchemeChanged) {
      this.incidentListeners.forEach((l) => l.dispose());
      this.incidentListeners = [];
      this.removeClusterDonutMarkers();
      this.map.removeLayer(LAYER.INCIDENT_CLUSTERS).removeSource(SOURCE.INCIDENT_CLUSTERS);

      donutMarkers = this.initClusterDonutMarkers({
        items: args.items,
        colorScheme: args.colorScheme,
        onClusterClick: args.onClusterClick,
      });
    }

    const source = this.map.getSource(SOURCE.INCIDENT_CLUSTERS);

    if (args.withCentrify) {
      const { center, bounds } = findLocationsCenter(args.items);
      this.map.setCenter({ lng: center.longitude, lat: center.latitude, smooth: true, bounds });
    }

    const colorKey = args.colorScheme.key || DEFAULT_INCIDENT_COLOR_KEY;
    const data = this.getIncidentsGeoJsonData({
      items: args.items,
      colorScheme: args.colorScheme,
      enrichProps: (item) => {
        const typeColorValue = Number(parseInt(item[colorKey] as string, 10));
        return {
          typeColorValue,
          color: args.colorScheme.scheme?.get(String(typeColorValue)) || 'white',
        };
      },
    });

    if (source) {
      this.map.updateSourceData(SOURCE.INCIDENT_CLUSTERS, data);
      donutMarkers.init();
    } else {
      const { clusterProperties, circleColors } = generateClusterIncidentProperties(args.colorScheme.scheme!, colorKey);

      this.map.addSource(SOURCE.INCIDENT_CLUSTERS, {
        type: 'geojson',
        data,
        cluster: true,
        clusterRadius: 80,
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
        clusterProperties: {
          // keep separate counts for each magnitude category in a cluster
          ...clusterProperties,
        } satisfies Record<keyof ClusterTypes, ExpressionSpecification>,
      });

      this.map.addLayer({
        id: LAYER.INCIDENT_CLUSTERS,
        source: SOURCE.INCIDENT_CLUSTERS,
        filter: ['!=', 'cluster', true],
        type: 'circle',
        paint: {
          'circle-color': circleColors,
          'circle-opacity': OPACITY,
          'circle-radius': {
            base: 1.75,
            stops: [
              [12, 5],
              [22, 10],
            ],
          },
        },
        metadata: {
          colorSchemaKey: args.colorScheme.key,
        },
      });
      this.applyVisibility(LAYER.INCIDENT_CLUSTERS);

      donutMarkers.init();

      // Assets should be over incidents — only when this drawer owns the assets layer,
      // otherwise we'd reorder a layer belonging to a separate AssetsMapDrawer.
      if (this.assetsDrawer) {
        this.map.moveLayer(LAYER.INCIDENT_CLUSTERS, LAYER.ASSETS);
      }

      const { dispose: disposeOnClick } = this.map.addLayerMouseEvents(LAYER.INCIDENT_CLUSTERS).on('click', (e) => {
        const properties = e.features![0].properties as RenderIncidentsItem;
        const feature = this.map?.queryRenderedFeatures(e.point, { layers: [LAYER.INCIDENT_CLUSTERS] }) || [];
        const ids = feature.map((item) => item.id as RenderIncidentsItem['id']);
        const locations = feature.map<LatLng>((item) => ({
          latitude: item.properties!.latitude,
          longitude: item.properties!.longitude,
        }));

        args.onClick(properties, { ids, locations });
      });

      this.incidentListeners.push({ dispose: disposeOnClick });

      const { dispose: disposeOnMouseover } = this.map
        .addLayerMouseEvents(LAYER.INCIDENT_CLUSTERS)
        .on('mouseover', (e) => {
          if (!e.lngLat) {
            return;
          }
          const properties = e.features![0].properties as RenderIncidentsItem;
          applyStaticEventCoords(e, properties);

          let title = properties.title;
          const feature = this.map?.queryRenderedFeatures(e.point, { layers: [LAYER.INCIDENT_CLUSTERS] }) || [];

          if (feature.length > 1) {
            title = `There are ${feature.length} alerts`;
          }

          this.map?.showPopup(e, {
            offset: [0, -8],
            layerId: LAYER.INCIDENT_CLUSTERS,
            content: createPopupContentElement({ title }),
          });
        });

      this.incidentListeners.push({ dispose: disposeOnMouseover });
    }
  };

  clear = (): void => {
    this.incidentListeners.forEach((l) => l.dispose());
    this.incidentListeners = [];

    this.removeClusterDonutMarkers();
    this.assetsDrawer?.clear();
    this.getOwnedLayerIds().forEach((id) => this.map?.removeLayer(id));
    this.getOwnedSourceIds().forEach((id) => this.map?.removeSource(id));
  };

  setVisible = (visible: boolean): void => {
    this.visible = visible;
    this.getOwnedLayerIds().forEach((id) => this.applyVisibility(id));
    this.assetsDrawer?.setVisible(visible);
    this.donutMarkers?.setVisible(visible);
  };
}
